// src/components/modals/EditSupplierModal.jsx
import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import PropTypes from 'prop-types'

const EditSupplierModal = ({ isOpen, onClose, onSubmit, supplier }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    contactPerson: '',
    email: '',
    address: '',
    accountInformation: {
      accountName: '',
      accountNumber: '',
      bankName: '',
      branchName: ''
    },
    isActive: true
  })
  
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Initialize form with supplier data
  useEffect(() => {
    if (isOpen && supplier) {
      setFormData({
        name: supplier.name || '',
        mobile: supplier.mobile || '',
        contactPerson: supplier.contactPerson || '',
        email: supplier.email || '',
        address: supplier.address || '',
        accountInformation: {
          accountName: supplier.accountInformation?.accountName || '',
          accountNumber: supplier.accountInformation?.accountNumber || '',
          bankName: supplier.accountInformation?.bankName || '',
          branchName: supplier.accountInformation?.branchName || ''
        },
        isActive: supplier.isActive !== false
      })
      setErrors({})
    }
  }, [isOpen, supplier])

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('account.')) {
      // Handle nested account information
      const accountField = name.replace('account.', '')
      setFormData(prev => ({
        ...prev,
        accountInformation: {
          ...prev.accountInformation,
          [accountField]: value
        }
      }))
    } else if (name === 'mobile') {
      // For mobile number, only allow digits and limit to 10
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }))
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validate mobile number (exactly 10 digits)
  const validateMobileNumber = (mobile) => {
    const mobileRegex = /^[0-9]{10}$/
    return mobileRegex.test(mobile)
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required'
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required'
    } else if (!validateMobileNumber(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits'
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required'
    }

    // Email validation (optional)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format'
      }
    }

    // Account information validation (all or none)
    const { accountName, accountNumber, bankName, branchName } = formData.accountInformation
    const hasAnyAccountInfo = accountName || accountNumber || bankName || branchName
    
    if (hasAnyAccountInfo) {
      if (!accountName.trim()) {
        newErrors.accountName = 'Account name is required when providing account information'
      }
      if (!accountNumber.trim()) {
        newErrors.accountNumber = 'Account number is required when providing account information'
      }
      if (!bankName.trim()) {
        newErrors.bankName = 'Bank name is required when providing account information'
      }
      if (!branchName.trim()) {
        newErrors.branchName = 'Branch name is required when providing account information'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)
    
    try {
      // Prepare data for API
      const submitData = {
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
        contactPerson: formData.contactPerson.trim(),
        email: formData.email.trim() || undefined,
        address: formData.address.trim() || undefined,
        isActive: formData.isActive
      }

      // Only include account information if at least one field is filled
      const { accountName, accountNumber, bankName, branchName } = formData.accountInformation
      if (accountName || accountNumber || bankName || branchName) {
        submitData.accountInformation = {
          accountName: accountName.trim(),
          accountNumber: accountNumber.trim(),
          bankName: bankName.trim(),
          branchName: branchName.trim()
        }
      }

      const response = await onSubmit(submitData)
      
      // If we get here without throwing, the submission was successful
      // The parent component handles the success message and modal closing
      
    } catch (error) {
      console.error('Error updating supplier:', error)
      
      // Handle different types of errors
      let errorMessage = 'Failed to update supplier'
      
      if (error.response?.data?.message) {
        // Backend validation error
        errorMessage = error.response.data.message
      } else if (error.message) {
        // Network or other error
        errorMessage = error.message
      }
      
      // Check for specific validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors
        const newErrors = {}
        
        // Map backend field errors to form fields
        if (backendErrors.name) newErrors.name = backendErrors.name
        if (backendErrors.mobile) newErrors.mobile = backendErrors.mobile
        if (backendErrors.email) newErrors.email = backendErrors.email
        if (backendErrors.contactPerson) newErrors.contactPerson = backendErrors.contactPerson
        if (backendErrors.accountName) newErrors.accountName = backendErrors.accountName
        if (backendErrors.accountNumber) newErrors.accountNumber = backendErrors.accountNumber
        if (backendErrors.bankName) newErrors.bankName = backendErrors.bankName
        if (backendErrors.branchName) newErrors.branchName = backendErrors.branchName
        
        setErrors(newErrors)
      }
      
      // Set general error message
      setErrors(prev => ({ ...prev, submit: errorMessage }))
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !supplier) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Supplier</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Supplier Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supplier name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Mobile Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile number *
                </label>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.mobile && (
                  <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Contact Person */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person *
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.contactPerson && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactPerson}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={loading}
              />
            </div>
          </div>

          {/* Account Information */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Account Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account name
                </label>
                <input
                  type="text"
                  name="account.accountName"
                  value={formData.accountInformation.accountName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.accountName && (
                  <p className="mt-1 text-sm text-red-600">{errors.accountName}</p>
                )}
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account number
                </label>
                <input
                  type="text"
                  name="account.accountNumber"
                  value={formData.accountInformation.accountNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.accountNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.accountNumber}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Bank Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank name
                </label>
                <input
                  type="text"
                  name="account.bankName"
                  value={formData.accountInformation.bankName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.bankName && (
                  <p className="mt-1 text-sm text-red-600">{errors.bankName}</p>
                )}
              </div>

              {/* Branch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch
                </label>
                <input
                  type="text"
                  name="account.branchName"
                  value={formData.accountInformation.branchName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                />
                {errors.branchName && (
                  <p className="mt-1 text-sm text-red-600">{errors.branchName}</p>
                )}
              </div>
            </div>
          </div>

          {/* Status Switch */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Status
                </label>
                {/* <p className="text-xs text-gray-500">
                  {formData.isActive ? 'Supplier is active and available for use' : 'Supplier is inactive and hidden'}
                </p> */}
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                {/* <span className="ml-3 text-sm text-gray-700">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span> */}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              placeholder="Additional notes about supplier products or anything.."
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={loading}
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

EditSupplierModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  supplier: PropTypes.object
}

export default EditSupplierModal